/**
 * Channel assignment for overlapping Drillz geometry interval layers.
 */

/**
 * @typedef {Object} GeometrySegment
 * @property {string} tableDefinitionId
 * @property {string} sourceRowId
 * @property {string} collarUuid
 * @property {number|null} depthFrom
 * @property {number|null} depthTo
 * @property {import('three').Vector3} start
 * @property {import('three').Vector3} end
 * @property {number} length
 * @property {import('three').Quaternion} quaternion
 * @property {import('three').Vector3} midpoint
 */

/**
 * Assign channels within one layer for overlapping depth intervals.
 * @param {Array<{ depthFrom: number, depthTo: number, key: string }>} items
 */
function assignChannelsGreedy(items) {
  const sorted = [...items].sort((a, b) => a.depthFrom - b.depthFrom || a.depthTo - b.depthTo);
  const assignments = new Map();
  const active = [];

  for (const item of sorted) {
    for (let j = active.length - 1; j >= 0; j--) {
      if (active[j].depthTo <= item.depthFrom) active.splice(j, 1);
    }
    const used = new Set(active.map((a) => a.channelIndex));
    let ch = 0;
    while (used.has(ch)) ch += 1;
    assignments.set(item.key, { channelIndex: ch, totalChannels: 1 });
    active.push({ ...item, channelIndex: ch });
  }

  let maxCh = 0;
  assignments.forEach((v) => { maxCh = Math.max(maxCh, v.channelIndex); });
  const total = maxCh + 1;
  assignments.forEach((v) => { v.totalChannels = total; });
  return assignments;
}

/**
 * @param {Array<{ layer: object, bundle: object, feature: object, start: import('three').Vector3, end: import('three').Vector3, midpoint: import('three').Vector3, length: number, quaternion: import('three').Quaternion }>} segments
 * @returns {Array<GeometrySegment & { channelIndex: number, totalChannels: number, globalChannelIndex: number, globalTotalChannels: number }>}
 */
export function assignLayerChannels(segments) {
  const byLayerCollar = new Map();
  for (const seg of segments) {
    const tid = seg.layer.tableDefinitionId;
    const cu = seg.feature.collarUuid || '';
    const groupKey = `${tid}:${cu}`;
    if (!byLayerCollar.has(groupKey)) byLayerCollar.set(groupKey, []);
    byLayerCollar.get(groupKey).push(seg);
  }

  const channelMap = new Map();
  let globalOffset = 0;
  let globalTotal = 0;

  const layerIds = [...new Set(segments.map((s) => s.layer.tableDefinitionId))];
  for (const tid of layerIds) {
    const collarGroups = [...byLayerCollar.entries()]
      .filter(([key]) => key.startsWith(`${tid}:`))
      .map(([, segs]) => segs);

    let layerMaxChannels = 1;
    for (const layerSegs of collarGroups) {
      const items = layerSegs
        .filter((s) => s.feature.depthFrom != null && s.feature.depthTo != null)
        .map((s) => ({
          depthFrom: s.feature.depthFrom,
          depthTo: s.feature.depthTo,
          key: `${tid}:${s.feature.sourceRowId}`,
        }));

      const layerAssign = assignChannelsGreedy(items);
      const collarTotal = items.length
        ? Math.max(...[...layerAssign.values()].map((v) => v.totalChannels), 1)
        : 1;
      layerMaxChannels = Math.max(layerMaxChannels, collarTotal);

      for (const seg of layerSegs) {
        const key = `${tid}:${seg.feature.sourceRowId}`;
        const ch = layerAssign.get(key) || { channelIndex: 0, totalChannels: collarTotal };
        channelMap.set(seg, {
          ...seg,
          channelIndex: ch.channelIndex,
          totalChannels: ch.totalChannels,
          globalChannelIndex: globalOffset + ch.channelIndex,
          globalTotalChannels: 0,
        });
      }
      globalOffset += collarTotal;
      globalTotal += collarTotal;
    }
  }

  return [...channelMap.values()].map((s) => ({
    ...s,
    globalTotalChannels: globalTotal || 1,
  }));
}

/**
 * Merge adjacent features with same visual value when continuousIntervals enabled.
 * Only merges within the same collar (never across holes).
 * @param {object[]} features
 * @param {object} bundle
 */
function mergeContinuousFeaturesForCollar(features, bundle) {
  const field = bundle.config.geometryVisualFieldCode;
  if (!field) return features;

  const sorted = [...features].sort(
    (a, b) => (a.depthFrom ?? 0) - (b.depthFrom ?? 0),
  );
  const merged = [];
  let current = null;

  for (const f of sorted) {
    const row = bundle.byRowId.get(String(f.sourceRowId));
    const val = row?.[field];
    if (!current) {
      current = { ...f, _mergedIds: [f.sourceRowId] };
      continue;
    }
    const curRow = bundle.byRowId.get(String(current.sourceRowId));
    const curVal = curRow?.[field];
    const sameCollar = f.collarUuid === current.collarUuid;
    const contiguous = Math.abs((f.depthFrom ?? 0) - (current.depthTo ?? 0)) < 0.01;
    if (sameCollar && contiguous && String(val) === String(curVal)) {
      current = {
        ...current,
        depthTo: f.depthTo,
        positions: [
          ...(current.positions || []),
          ...(f.positions?.slice(1) || []),
        ],
        _mergedIds: [...(current._mergedIds || []), f.sourceRowId],
      };
    } else {
      merged.push(current);
      current = { ...f, _mergedIds: [f.sourceRowId] };
    }
  }
  if (current) merged.push(current);
  return merged;
}

export function mergeContinuousFeatures(features, bundle) {
  if (!bundle.config.continuousIntervals || !features.length) return features;

  const byCollar = new Map();
  for (const f of features) {
    const cu = f.collarUuid || '';
    if (!byCollar.has(cu)) byCollar.set(cu, []);
    byCollar.get(cu).push(f);
  }

  return [...byCollar.values()].flatMap((collarFeatures) =>
    mergeContinuousFeaturesForCollar(collarFeatures, bundle),
  );
}
